"use client";

import { useCallback, useEffect, useState } from "react";

/** Appearance modes mirror `DSAppearanceMode` on iOS. */
export const DSAppearanceMode = {
	system: "system",
	light: "light",
	dark: "dark",
} as const;

export type DSAppearanceMode =
	(typeof DSAppearanceMode)[keyof typeof DSAppearanceMode];

export const DSAppearanceModeAll: readonly DSAppearanceMode[] = [
	DSAppearanceMode.system,
	DSAppearanceMode.light,
	DSAppearanceMode.dark,
];

export const DSAppearanceStorageKey = "artist.ds.appearance";

/** Resolved color scheme derived from mode + the user's OS preference. */
export type DSResolvedScheme = "light" | "dark";

function readSystemScheme(): DSResolvedScheme {
	if (typeof window === "undefined") return "dark";
	return window.matchMedia?.("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function readStoredMode(): DSAppearanceMode {
	if (typeof window === "undefined") return DSAppearanceMode.system;
	const raw = window.localStorage?.getItem(DSAppearanceStorageKey);
	if (
		raw === DSAppearanceMode.light ||
		raw === DSAppearanceMode.dark ||
		raw === DSAppearanceMode.system
	) {
		return raw;
	}
	return DSAppearanceMode.system;
}

export interface DSAppearanceState {
	readonly mode: DSAppearanceMode;
	readonly scheme: DSResolvedScheme;
	readonly setMode: (mode: DSAppearanceMode) => void;
}

/**
 * resolveScheme — pure helper used by the early inline script and the hook so
 * the chosen mode maps to an effective scheme without re-deriving it inline.
 */
export function resolveScheme(
	mode: DSAppearanceMode,
	systemScheme: DSResolvedScheme,
): DSResolvedScheme {
	return mode === DSAppearanceMode.system ? systemScheme : mode;
}

/**
 * applyThemeToDocument — writes `data-theme="<scheme>"` and
 * `data-appearance="<mode>"` onto `<html>` so the CSS cascade (which keys
 * off `[data-theme="light"]`) switches even when the OS preference is dark.
 * Mirrors iOS `DSAppearanceMode.preferredColorScheme` application.
 */
export function applyThemeToDocument(
	mode: DSAppearanceMode,
	scheme: DSResolvedScheme,
): void {
	if (typeof document === "undefined") return;
	const root = document.documentElement;
	root.dataset.theme = scheme;
	root.dataset.appearance = mode;
	root.style.colorScheme = scheme;
}

/**
 * useDSAppearance — persists the chosen mode to localStorage, resolves the
 * effective color scheme against `prefers-color-scheme`, and applies
 * `data-theme` to `<html>` so the DS cascade switches. Default appearance is
 * system (the contract default), which expands to the OS preference at
 * runtime.
 *
 * Multiple hook instances stay in sync via the `storage` event — e.g. when
 * the Settings page calls `setMode("light")`, the root shell's instance
 * re-reads the stored value so its own state matches the new document theme.
 */
export function useDSAppearance(): DSAppearanceState {
	const [mode, setModeState] = useState<DSAppearanceMode>(
		DSAppearanceMode.system,
	);
	const [systemScheme, setSystemScheme] = useState<DSResolvedScheme>("light");

	useEffect(() => {
		setModeState(readStoredMode());
		setSystemScheme(readSystemScheme());
		const media = window.matchMedia?.("(prefers-color-scheme: dark)");
		const onChange = () => setSystemScheme(readSystemScheme());
		media?.addEventListener("change", onChange);
		// Cross-instance sync: when another `useDSAppearance` instance writes
		// `localStorage[DSAppearanceStorageKey]` (e.g. from Settings), this
		// instance re-reads so its `mode` matches the new document theme.
		const onStorage = (event: StorageEvent) => {
			if (event.key !== DSAppearanceStorageKey) return;
			setModeState(readStoredMode());
		};
		window.addEventListener?.("storage", onStorage);
		return () => {
			media?.removeEventListener("change", onChange);
			window.removeEventListener?.("storage", onStorage);
		};
	}, []);

	const scheme = resolveScheme(mode, systemScheme);

	// Apply theme to <html> whenever mode/scheme changes.
	useEffect(() => {
		applyThemeToDocument(mode, scheme);
	}, [mode, scheme]);

	const setMode = useCallback((next: DSAppearanceMode) => {
		setModeState(next);
		try {
			window.localStorage?.setItem(DSAppearanceStorageKey, next);
		} catch {
			/* localStorage unavailable — keep in-memory state only. */
		}
		// Dispatch a synthetic storage event so other hook instances in the
		// same document re-read the stored value (the native `storage` event
		// only fires in *other* documents, not the one that wrote).
		try {
			window.dispatchEvent(
				new StorageEvent("storage", {
					key: DSAppearanceStorageKey,
					newValue: next,
				}),
			);
		} catch {
			/* StorageEvent constructor unavailable — skip cross-instance sync. */
		}
	}, []);

	return { mode, scheme, setMode };
}

/**
 * useArtistAppearance — alias for `useDSAppearance` mirroring the iOS
 * `DSAppearanceMode` golden source naming. Callers can use either name.
 */
export const useArtistAppearance = useDSAppearance;

/** Display labels for the showcase toggle (system/light/dark). */
export const DSAppearanceModeLabel: Readonly<Record<DSAppearanceMode, string>> =
	{
		[DSAppearanceMode.system]: "系统",
		[DSAppearanceMode.light]: "浅色",
		[DSAppearanceMode.dark]: "深色",
	};
